import React, { useState } from 'react'
import { PageLayout } from '../components/common/PageLayout'
import { Section } from '../components/common/DesignComponents'
import { useNavigation } from '../contexts/NavigationContext'

export function HelpPage(): React.ReactElement {
  const { openModal } = useNavigation()
  const [chatOpen, setChatOpen] = useState(false)
  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'How do I create my first child profile?',
          a: 'Click on "Create Child" from the dashboard, enter your child\'s name and age, then customize their personality traits. Your child will receive a starter pack of coins and items!'
        },
        {
          q: 'What age is BabyKingdom suitable for?',
          a: 'BabyKingdom is designed for children ages 3-12. All content is age-appropriate and tailored to different development stages.'
        },
      ]
    },
    {
      category: 'Gameplay',
      questions: [
        {
          q: 'How do I earn coins?',
          a: 'Coins are earned by playing games, completing daily challenges, and achieving milestones. Premium members earn coins at 2x rate!'
        },
        {
          q: 'What can I buy with coins?',
          a: 'Use coins to purchase cosmetic items, power-ups, special accessories, and unlock premium areas in the game.'
        },
      ]
    },
    {
      category: 'Parental Controls',
      questions: [
        {
          q: 'How do I set screen time limits?',
          a: 'Go to Settings > Parental Controls and set daily playtime limits. Your child will receive a friendly reminder when time is running out.'
        },
        {
          q: 'Can I monitor my child\'s progress?',
          a: 'Yes! The Dashboard shows detailed stats about your child\'s learning, play time, achievements, and development progress.'
        },
      ]
    },
    {
      category: 'Account & Security',
      questions: [
        {
          q: 'Is my child\'s data safe?',
          a: 'We use industry-standard encryption and follow COPPA compliance guidelines. Your child\'s data is never shared with third parties.'
        },
        {
          q: 'How do I reset my password?',
          a: 'Click "Forgot Password" on the login page, verify your email, and follow the instructions to create a new password.'
        },
      ]
    },
  ]

  return (
    <PageLayout title="Help & Support" subtitle="Find answers to common questions">
      <Section title="Frequently Asked Questions">
        <div className="space-y-8">
          {faqs.map((faqCategory, idx) => (
            <div key={idx}>
              <h3 className="text-2xl font-black text-purple-700 mb-4 pb-3 border-b-4 border-purple-200">
                {faqCategory.category}
              </h3>
              <div className="space-y-4">
                {faqCategory.questions.map((faq, qIdx) => (
                  <details key={qIdx} className="group border-4 border-purple-200 rounded-2xl overflow-hidden">
                    <summary className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 cursor-pointer hover:from-purple-100 hover:to-pink-100 font-black text-purple-700 flex justify-between items-center">
                      {faq.q}
                      <span className="group-open:rotate-180 transition">▼</span>
                    </summary>
                    <div className="p-6 bg-white text-gray-700 font-semibold border-t-4 border-purple-200">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Still Need Help?">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border-4 border-blue-200 text-center">
            <div className="text-5xl mb-3">💬</div>
            <h4 className="font-black text-blue-700 mb-2">Live Chat</h4>
            <p className="text-gray-700 font-bold mb-4">Chat with our support team in real-time</p>
            <button
              onClick={() => {
                setChatOpen(!chatOpen)
                openModal('contact' as any)
              }}
              className="px-6 py-2 bg-blue-400 text-white font-black rounded-full hover:bg-blue-500"
            >
              {chatOpen ? '💬 Chat Open' : 'Open Chat'}
            </button>
          </div>
          <div className="p-8 bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl border-4 border-pink-200 text-center">
            <div className="text-5xl mb-3">📧</div>
            <h4 className="font-black text-pink-700 mb-2">Email Support</h4>
            <p className="text-gray-700 font-bold mb-4">Send us an email and we'll respond within 24 hours</p>
            <a href="mailto:support@babykingdom.com" className="px-6 py-2 bg-pink-400 text-white font-black rounded-full inline-block hover:bg-pink-500">Email Us</a>
          </div>
          <div className="p-8 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl border-4 border-purple-200 text-center">
            <div className="text-5xl mb-3">👥</div>
            <h4 className="font-black text-purple-700 mb-2">Community</h4>
            <p className="text-gray-700 font-bold mb-4">Join our community forum for tips and tricks</p>
            <button onClick={() => openModal('contact' as any)} className="px-6 py-2 bg-purple-400 text-white font-black rounded-full hover:bg-purple-500">Visit Forum</button>
          </div>
        </div>
      </Section>
    </PageLayout>
  )
}
